import { useRef, useState, useLayoutEffect } from 'react';

export function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Initialize size
    let rafId: number | null = null;
    let lastWidth = -1;
    let lastHeight = -1;

    const commitSize = (width: number, height: number) => {
      if (!Number.isFinite(width) || !Number.isFinite(height)) return;
      const nextWidth = Math.round(width);
      const nextHeight = Math.round(height);
      if (nextWidth === lastWidth && nextHeight === lastHeight) return;
      lastWidth = nextWidth;
      lastHeight = nextHeight;
      setSize(prev => (prev.width === nextWidth && prev.height === nextHeight)
        ? prev
        : { width: nextWidth, height: nextHeight });
    };

    commitSize(el.clientWidth, el.clientHeight);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(entries => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      const cr = entry.contentRect;
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        commitSize(cr.width, cr.height);
      });
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, []);

  return [ref, size] as const;
}

export default useElementSize;
